import { apiClient } from '@/services/api/client';
import { OrphanDocument } from '@/types/documents';

const API_BASE = '/document-comparison';

type UploadResult =
  | { processId: number; is_redirect?: false; aguardando_veloster?: false }
  | { processId: number; is_redirect: true; redirect_message?: string; aguardando_veloster?: false }
  /**
   * Processo sem Gemini e Veloster indisponível: o envio foi guardado e o
   * processo é criado sozinho quando o Veloster voltar. Ainda não há processo.
   */
  | { processId: null; aguardando_veloster: true; message?: string };

export interface SigraIdLookup {
  vinculo: 'livre' | 'mesma_empresa' | 'outra_empresa';
  process?: { id: number; process_number: string | null };
}

export const documentsService = {
  async getSigraIdLookup(sigraId: string): Promise<SigraIdLookup> {
    const response = await apiClient.get<SigraIdLookup>(`${API_BASE}/sigra-id/${encodeURIComponent(sigraId)}`);
    return response.data;
  },

  async getDocumentFile(processId: number, documentId: number): Promise<{ blob: Blob; fileName?: string }> {
    const url = `${API_BASE}/processes/${processId}/documents/${documentId}/file?t=${Date.now()}`;
    const response = await apiClient.get<Blob>(url, { responseType: 'blob' });

    const cd = response.headers?.['content-disposition'];
    const match = cd?.match(/filename="?([^"]+)"?/);
    const fileName = match?.[1];

    return { blob: response.data, fileName };
  },

  async listOrphanDocuments(filters?: OrphanDocumentsFilters): Promise<OrphanDocument[]> {
    const qs = toSearchParams({
      reference: filters?.reference,
      base_type: filters?.base_type,
    }).toString();

    const url = qs ? `${API_BASE}/orphan-documents?${qs}` : `${API_BASE}/orphan-documents`;

    const response = await apiClient.get<OrphanDocument[]>(url);
    return response.data ?? [];
  },

  async deleteDocument(processId: number, documentId: number) {
    await apiClient.delete(`${API_BASE}/processes/${processId}/documents/${documentId}`);
  },

  async exportDocumentItems(processId: number, documentId: number): Promise<{ blob: Blob; fileName?: string }> {
    const url = `${API_BASE}/processes/${processId}/documents/${documentId}/items/export`;
    const response = await apiClient.get<Blob>(url, { responseType: 'blob' });

    const cd = response.headers?.['content-disposition'];
    const match = cd?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const fileName = match?.[1]?.replace(/['"]/g, '') || `document_${documentId}_items.xlsx`;

    return { blob: response.data, fileName };
  },

  async updateDocumentName(
    processId: number,
    documentId: number,
    inputEditName: string | null,
  ): Promise<{ message: string; input_edit_name: string | null }> {
    const response = await apiClient.patch(
      `${API_BASE}/processes/${processId}/documents/${documentId}/name`,
      { input_edit_name: inputEditName },
    );
    return response.data;
  },

  async reprocessDocument(processId: number, documentId: number): Promise<{ message: string }> {
    const url = `${API_BASE}/processes/${processId}/documents/${documentId}/reprocess`;
    const response = await apiClient.post(url);
    return response.data;
  },

  async updateDocumentType(
    processId: number,
    documentId: number,
    baseType: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.patch(
      `${API_BASE}/processes/${processId}/documents/${documentId}/type`,
      { base_type: baseType },
    );
    return response.data;
  },

  async getSapTagOverride(
    processId: number,
    documentId: number,
  ): Promise<{ data: Record<string, any> | null }> {
    const response = await apiClient.get(
      `${API_BASE}/processes/${processId}/documents/${documentId}/sap-tags`,
    );
    return response.data;
  },

  async saveSapTagOverride(
    processId: number,
    documentId: number,
    data: Record<string, any>,
  ): Promise<{ message: string }> {
    const response = await apiClient.patch(
      `${API_BASE}/processes/${processId}/documents/${documentId}/sap-tags`,
      { data },
    );
    return response.data;
  },

  async requestSapLaunch(
    processId: number,
    documentId: number,
    force = false,
  ): Promise<{ message: string }> {
    const response = await apiClient.post(
      `${API_BASE}/processes/${processId}/documents/${documentId}/sap-launch`,
      { force },
    );
    return response.data;
  },

  async getSapLaunchStatus(
    processId: number,
    documentId: number,
  ): Promise<{
    status: string | null;
    requestedAt: string | null;
    processedAt: string | null;
    resultMessage: string | null;
  }> {
    const response = await apiClient.get(
      `${API_BASE}/processes/${processId}/documents/${documentId}/sap-launch-status`,
    );
    return response.data;
  },

  async getSapKnowledgeDomains(): Promise<
    Record<string, { codigo: string; descricao: string | null; dimensao: string | null }[]>
  > {
    const response = await apiClient.get(`${API_BASE}/sap-launch-domains`);
    return response.data;
  },

  async getSapInvoiceDomains(): Promise<
    Record<string, { codigo: string; descricao: string | null; dimensao: string | null }[]>
  > {
    const response = await apiClient.get(`${API_BASE}/sap-launch-domains/invoice`);
    return response.data;
  },

  async getDocumentVersions(processId: number, documentId: number): Promise<DocumentVersion[]> {
    const url = `${API_BASE}/processes/${processId}/documents/${documentId}/versions`;
    const response = await apiClient.get<DocumentVersion[]>(url);
    return response.data ?? [];
  },

  async getOrphanDocumentFile(orphanDocumentId: number): Promise<{ blob: Blob; mimeType: string; fileName?: string }> {
    const response = await apiClient.get<Blob>(`${API_BASE}/orphan-documents/${orphanDocumentId}/file`, {
      responseType: 'blob',
    });

    const mimeType =
      response.headers?.['content-type'] ||
      response.headers?.['Content-Type'] ||
      'application/pdf';

    const cd = response.headers?.['content-disposition'];
    const match = cd?.match(/filename="?([^"]+)"?/);
    const fileName = match?.[1];

    return { blob: response.data, mimeType, fileName };
  },

  async addDocuments(params: {
    processId: number;
    files: File[];
    fileTypes?: string[]; // quando não for auto-identificação
    signal?: AbortSignal;
  }) {
    const fd = new FormData();

    const acceptsMultiple = params.files.length > 1;
    if (acceptsMultiple) params.files.forEach((f) => fd.append('files', f));
    else fd.append('file', params.files[0]);

    if (params.fileTypes) fd.append('fileTypes', JSON.stringify(params.fileTypes));

    // NÃO setar Content-Type: o browser define automaticamente multipart/form-data com boundary
    const res = await apiClient.post(`${API_BASE}/processes/${params.processId}/add-document`, fd, {
      signal: params.signal,
    });

    return res.data as {
      documentIds?: number[];
      processing?: boolean;
      count?: number;
      message?: string;
      /** Arquivos que não entraram; o resto do lote entrou normalmente. */
      falhas?: Array<{ arquivo: string; erro: string }>;
    };
  },

  async uploadDocuments(formData: FormData, token?: string, timeoutMs = 300000): Promise<UploadResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await apiClient.post('/document-comparison/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          // NÃO setar Content-Type aqui (FormData)
        },
        signal: controller.signal,
      });

      return res.data as UploadResult;
    } finally {
      clearTimeout(timeoutId);
    }
  },
};

const OPEN_DEBOUNCE_MS = 1200;
let lastOpenTime = 0;
let isOpening = false; // Flag para evitar chamadas simultâneas

export function openBlobInNewTab(blob: Blob) {
  const now = Date.now();

  if (isOpening) return;
  if (now - lastOpenTime < OPEN_DEBOUNCE_MS) return;

  isOpening = true;
  lastOpenTime = now;

  const url = window.URL.createObjectURL(blob);

  try {
    const win = window.open(url, '_blank');

    if (!win) {
      console.warn('Popup bloqueado. Por favor, permita popups para este site.');
    }
  } finally {
    setTimeout(() => {
      isOpening = false;
    }, 500);

    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  }
}

export type DocumentVersion = {
  id: number;
  original_name: string;
  document_reference: string | null;
  uploaded_at: string;
  archived_at: string;
  uploaded_by: number | null;
  file_size: number | null;
};

type OrphanDocumentsFilters = {
  reference?: string;
  base_type?: string;
};

function toSearchParams(params?: Record<string, string | undefined>) {
  const sp = new URLSearchParams();
  if (!params) return sp;

  Object.entries(params).forEach(([k, v]) => {
    if (!v) return;
    sp.append(k, v);
  });

  return sp;
}